import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { GAME_ID, formatMoney } from "../utils/helpers";
import { styles } from "../styles/theme";

function VictoryPage() {
  const [winner, setWinner] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNewGame = () => {
    if (window.confirm("¿Quieres limpiar tu sesión para poder jugar de nuevo cuando el juego se reinicie?")) {
      localStorage.removeItem("panGamePlayerId");
      window.location.reload();
    }
  };

  useEffect(() => {
    const fetchWinner = async () => {
      try {
        const playersCol = collection(db, "games", GAME_ID, "players");
        const snapshot = await getDocs(playersCol);
        
        const players = [];
        snapshot.forEach((doc) => {
          players.push({ id: doc.id, ...doc.data() });
        });

        // Ordenar por panes (breads) y luego por dinero
        players.sort((a, b) => {
          const breadsA = a.breads || 0;
          const breadsB = b.breads || 0;
          if (breadsB !== breadsA) return breadsB - breadsA;
          
          const moneyA = a.money || 0;
          const moneyB = b.money || 0;
          return moneyB - moneyA;
        });

        setAllPlayers(players);
        if (players.length > 0) {
          setWinner(players[0]);
        }

        // Obtener jugadores eliminados de los logs y calcular dinero total obtenido
        const historialCol = collection(db, "games", GAME_ID, "historial");
        const historialSnap = await getDocs(historialCol);
        
        const eliminatedMap = {};
        const totalMoneyByPlayer = {}; // Acumulador de dinero ganado por jugador
        const eliminationRoundByPlayer = {}; // Mapa de jugador a ronda de eliminación
        const eliminationByName = {}; // Mapa alternativo por nombre (por si los IDs no coinciden)
        
        // Inicializar todos los jugadores con dinero inicial
        players.forEach(player => {
          totalMoneyByPlayer[player.id] = 5; // Dinero inicial
        });
        
        historialSnap.forEach((doc) => {
          const log = doc.data();
          
          // Jugadores eliminados
          if (log.type === "player_eliminated" && log.data) {
            const round = log.data.round;
            const playerId = log.data.playerId;
            const playerName = log.data.playerName;
            
            if (!eliminatedMap[round]) {
              eliminatedMap[round] = [];
            }
            eliminatedMap[round].push({
              name: playerName,
              money: log.data.money || 0,
            });
            
            // Guardar la ronda de eliminación del jugador (por ID y por nombre)
            eliminationRoundByPlayer[playerId] = round;
            if (playerName) {
              eliminationByName[playerName.toLowerCase()] = round;
            }
          }
          
          // Calcular dinero total ganado (minijuegos + becas)
          if ((log.type === "minigame_reward" || log.type === "stipend_distributed") && log.data) {
            const playerId = log.data.playerId;
            const amount = log.data.amount || 0;
            if (totalMoneyByPlayer[playerId] !== undefined) {
              totalMoneyByPlayer[playerId] += amount;
              console.log(`${log.type}: ${log.data.playerName} +${amount} (total: ${totalMoneyByPlayer[playerId]})`);
            }
          }
        });
        
        // Obtener la ronda final (la última ronda jugada)
        const gameRef = doc(db, "games", GAME_ID);
        const gameSnap = await getDoc(gameRef);
        const finalRound = gameSnap.data()?.currentRound || 1;
        
        // Agregar totalMoneyEarned y eliminationRound a cada jugador
        players.forEach(player => {
          player.totalMoneyEarned = totalMoneyByPlayer[player.id] || 5; // Si no hay logs, solo tiene los 5 iniciales
          
          // Buscar eliminationRound por ID primero, luego por nombre
          player.eliminationRound = eliminationRoundByPlayer[player.id] 
            || eliminationByName[player.name?.toLowerCase()] 
            || null;
          
          console.log(`Procesando ${player.name} (ID: ${player.id}):`, {
            active: player.active,
            eliminationRound: player.eliminationRound,
            fromId: eliminationRoundByPlayer[player.id],
            fromName: eliminationByName[player.name?.toLowerCase()],
            finalRound
          });
          
          // Si el jugador está inactivo pero no tiene eliminationRound en los logs,
          // asumimos que fue eliminado en la última ronda
          if (player.active === false && !player.eliminationRound) {
            console.warn(`Jugador ${player.name} (${player.id}) está eliminado pero no tiene log de eliminación - asignando ronda ${finalRound}`);
            player.eliminationRound = finalRound;
          }
        });

        console.log("Elimination rounds by ID:", eliminationRoundByPlayer);
        console.log("Elimination rounds by name:", eliminationByName);
        console.log("Players:", players.map(p => ({ id: p.id, name: p.name, active: p.active, eliminationRound: p.eliminationRound })));

        setAllPlayers(players);
        if (players.length > 0) {
          setWinner(players[0]);
        }
      } catch (error) {
        console.error("Error al obtener el ganador:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinner();
  }, []);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>Cargando resultados...</h1>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>🎉 ¡Juego Terminado! 🎉</h1>
        
        {winner && (
          <div style={victoryStyles.winnerSection}>
            <h2 style={victoryStyles.winnerTitle}>🏆 Ganador 🏆</h2>
            <div style={victoryStyles.winnerCard}>
              <p style={victoryStyles.winnerName}>{winner.name}</p>
              <div style={victoryStyles.winnerStats}>
                <div>
                  <span style={victoryStyles.statLabel}>Dinero total obtenido:</span>
                  <span style={victoryStyles.statValue}> {formatMoney(winner.totalMoneyEarned || 5)}</span>
                </div>
                <div>
                  <span style={victoryStyles.statLabel}>Dinero restante:</span>
                  <span style={victoryStyles.statValue}> {formatMoney(winner.money || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {allPlayers.length > 1 && (
          <div style={victoryStyles.rankingSection}>
            <h3 style={styles.sectionTitle}>📊 Clasificación Final</h3>
            <ol style={victoryStyles.rankingList}>
              {allPlayers.map((player, index) => (
                <li key={player.id} style={victoryStyles.rankingItem}>
                  <span style={victoryStyles.rankPosition}>{index + 1}.</span>
                  <span style={victoryStyles.rankName}>
                    {player.name}
                    {player.eliminationRound && (
                      <span style={{ fontSize: "12px", color: "#f44336", marginLeft: "8px" }}>
                        (Eliminado en ronda {player.eliminationRound})
                      </span>
                    )}
                  </span>
                  <span style={victoryStyles.rankStats}>
                    Obtenido: {formatMoney(player.totalMoneyEarned || 5)} · Restante: {formatMoney(player.money || 0)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div style={victoryStyles.messageSection}>
          <p style={victoryStyles.message}>
            ¡Gracias por jugar! 🎮
          </p>
          <p style={victoryStyles.subMessage}>
            El objetivo era conseguir la mayor cantidad de pan. 
            {winner && ` ¡Felicidades ${winner.name} por la victoria!`}
          </p>
          <button 
            onClick={handleNewGame}
            style={victoryStyles.newGameButton}
          >
            🔄 Limpiar sesión para nueva partida
          </button>
        </div>
      </div>
    </div>
  );
}

const victoryStyles = {
  winnerSection: {
    marginTop: 20,
    marginBottom: 24,
  },
  winnerTitle: {
    fontSize: 18,
    color: "#FFE5A8",
    textAlign: "center",
    margin: "0 0 12px 0",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  winnerCard: {
    background: "linear-gradient(135deg, rgba(0, 155, 255, 0.2), rgba(0, 100, 200, 0.1))",
    border: "2px solid rgba(0, 155, 255, 0.6)",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 0 30px rgba(0, 155, 255, 0.4)",
  },
  winnerName: {
    fontSize: 24,
    fontWeight: 700,
    color: "#5DB6FF",
    textAlign: "center",
    margin: "0 0 12px 0",
  },
  winnerStats: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontSize: 14,
  },
  statLabel: {
    color: "#A8D5FF",
    fontWeight: 600,
  },
  statValue: {
    color: "#E8F6FF",
    fontWeight: 700,
    fontSize: 16,
  },
  rankingSection: {
    marginTop: 24,
  },
  rankingList: {
    listStyle: "none",
    padding: 0,
    margin: "12px 0 0 0",
  },
  rankingItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    marginBottom: 6,
    background: "rgba(0, 40, 80, 0.6)",
    border: "1px solid rgba(0, 155, 255, 0.3)",
    borderRadius: 8,
    fontSize: 13,
  },
  rankPosition: {
    fontWeight: 700,
    color: "#5DB6FF",
    minWidth: 24,
  },
  rankName: {
    flex: 1,
    color: "#E8F6FF",
    fontWeight: 600,
  },
  rankStats: {
    color: "#A8D5FF",
    fontSize: 12,
  },
  messageSection: {
    marginTop: 24,
    padding: 16,
    background: "rgba(0, 155, 255, 0.1)",
    borderRadius: 8,
    border: "1px solid rgba(0, 155, 255, 0.3)",
  },
  message: {
    fontSize: 16,
    fontWeight: 600,
    color: "#5DB6FF",
    textAlign: "center",
    margin: "0 0 8px 0",
  },
  subMessage: {
    fontSize: 13,
    color: "#A8D5FF",
    textAlign: "center",
    margin: 0,
    lineHeight: 1.5,
  },
  newGameButton: {
    marginTop: 16,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    color: "#E8F6FF",
    background: "linear-gradient(135deg, rgba(0, 155, 255, 0.3), rgba(0, 100, 200, 0.2))",
    border: "1px solid rgba(0, 155, 255, 0.5)",
    borderRadius: 8,
    cursor: "pointer",
    width: "100%",
    transition: "all 0.3s ease",
  },
  eliminatedSection: {
    marginTop: 24,
    padding: 16,
    background: "rgba(255, 0, 0, 0.1)",
    borderRadius: 12,
    border: "1px solid rgba(255, 100, 100, 0.3)",
  },
  roundEliminatedGroup: {
    marginTop: 12,
  },
  roundEliminatedTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#FF8A8A",
    margin: "8px 0 8px 0",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  eliminatedList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  eliminatedItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: "rgba(100, 0, 0, 0.3)",
    borderRadius: 6,
    border: "1px solid rgba(255, 100, 100, 0.2)",
  },
  eliminatedName: {
    color: "#FFB3B3",
    fontWeight: 600,
    fontSize: 13,
  },
  eliminatedMoney: {
    color: "#FF8A8A",
    fontSize: 12,
    opacity: 0.8,
  },
};

export default VictoryPage;
